import { useRef, useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useTr } from '../contexts/LanguageContext';

const useInView = (threshold = 0.08) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const tokenData = [
  { name_en: 'Community',          name_ko: '커뮤니티',         value: 20, tokens: '200,000,000', color: '#30d5c8' },
  { name_en: 'Team',               name_ko: '팀',              value: 15, tokens: '150,000,000', color: '#55d532' },
  { name_en: 'Ecosystem Rewards',  name_ko: '생태계 보상',      value: 16, tokens: '160,000,000', color: '#4de8d0' },
  { name_en: 'Liquidity',          name_ko: '유동성',           value: 12, tokens: '120,000,000', color: '#3da020' },
  { name_en: 'Strategic Round',    name_ko: '전략적 라운드',    value: 10, tokens: '100,000,000', color: '#27d555' },
  { name_en: 'Marketing',          name_ko: '마케팅',           value: 8,  tokens: '80,000,000',  color: '#7bed9f' },
  { name_en: 'Treasury',           name_ko: '재무',             value: 8,  tokens: '80,000,000',  color: '#27b5aa' },
  { name_en: 'Reserve',            name_ko: '준비금',           value: 6,  tokens: '60,000,000',  color: '#1d8a80' },
  { name_en: 'Strategic Partners', name_ko: '전략적 파트너',    value: 5,  tokens: '50,000,000',  color: '#0d7a70' },
];

const vestingData = [
  { name_en: 'Community',         name_ko: '커뮤니티',       pct: '20%', tokens: '200,000,000', tge_en: '50%',  tge_ko: '50%',  cliff_en: '1 month',   cliff_ko: '1개월',  vest_en: 'Remainder per schedule', vest_ko: '잔량 일정대로' },
  { name_en: 'Ecosystem Rewards', name_ko: '생태계 보상',    pct: '16%', tokens: '160,000,000', tge_en: '30%',  tge_ko: '30%',  cliff_en: '1 month',   cliff_ko: '1개월',  vest_en: 'Remainder per schedule', vest_ko: '잔량 일정대로' },
  { name_en: 'Team',              name_ko: '팀',             pct: '15%', tokens: '150,000,000', tge_en: '0%',   tge_ko: '0%',   cliff_en: '12 months', cliff_ko: '12개월', vest_en: '24 months linear',       vest_ko: '24개월 선형' },
  { name_en: 'Liquidity',        name_ko: '유동성',          pct: '12%', tokens: '120,000,000', tge_en: '100%', tge_ko: '100%', cliff_en: 'None',      cliff_ko: '없음',   vest_en: 'CEX/DEX',                vest_ko: 'CEX/DEX' },
  { name_en: 'Strategic Round',  name_ko: '전략적 라운드',   pct: '10%', tokens: '100,000,000', tge_en: '0%',   tge_ko: '0%',   cliff_en: '12 months', cliff_ko: '12개월', vest_en: 'Linear',                 vest_ko: '선형' },
  { name_en: 'Marketing',        name_ko: '마케팅',          pct: '8%',  tokens: '80,000,000',  tge_en: '25%',  tge_ko: '25%',  cliff_en: '1 month',   cliff_ko: '1개월',  vest_en: 'Remainder per schedule', vest_ko: '잔량 일정대로' },
  { name_en: 'Treasury',         name_ko: '재무',            pct: '8%',  tokens: '80,000,000',  tge_en: '0%',   tge_ko: '0%',   cliff_en: '6 months',  cliff_ko: '6개월',  vest_en: 'Per schedule',           vest_ko: '일정대로' },
  { name_en: 'Reserve',          name_ko: '준비금',          pct: '6%',  tokens: '60,000,000',  tge_en: '0%',   tge_ko: '0%',   cliff_en: 'None',      cliff_ko: '없음',   vest_en: 'Locked',                 vest_ko: '잠금' },
  { name_en: 'Strategic Partners',name_ko: '전략적 파트너',  pct: '5%',  tokens: '50,000,000',  tge_en: '0%',   tge_ko: '0%',   cliff_en: '12 months', cliff_ko: '12개월', vest_en: '12 months linear',       vest_ko: '12개월 선형' },
];

const CustomTooltip = ({ active, payload, lang }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div className="glass rounded-lg p-3 border border-white/10">
        <p className="text-sm font-semibold text-white">{lang === 'ko' ? d.name_ko : d.name_en}</p>
        <p className="font-mono text-sm" style={{ color: d.color }}>{d.value}% ({d.tokens})</p>
      </div>
    );
  }
  return null;
};

const TokenomicsSection = () => {
  const t = useTr();
  const { ref, inView } = useInView();
  const [active, setActive] = useState(null);
  const [lang, setLang] = useState('en');

  useEffect(() => {
    const saved = localStorage.getItem('sb-lang');
    if (saved) setLang(saved);
    const onStorage = () => setLang(localStorage.getItem('sb-lang') || 'en');
    window.addEventListener('storage', onStorage);
    // Watch for class change on html
    const observer = new MutationObserver(() => {
      setLang(document.documentElement.classList.contains('lang-ko') ? 'ko' : 'en');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => { window.removeEventListener('storage', onStorage); observer.disconnect(); };
  }, []);

  const utility = [
    { n: '01', label: t('Platform Payment', '플랫폼 결제'),      desc: t('Premium AI agent tasks and AI builds settled in $SWARM.', '프리미엄 AI 에이전트 작업 및 AI 빌드를 $SWARM으로 결제합니다.') },
    { n: '02', label: t('Access Tiers', '접근 등급'),             desc: t('Subscription plans and premium model access.', '구독 플랜 및 프리미엄 모델 접근.') },
    { n: '03', label: t('Governance', '거버넌스'),                desc: t('Vote on protocol upgrades, new agents, fees, treasury, ecosystem grants.', '프로토콜 업그레이드, 신규 에이전트, 수수료, 재무, 생태계 지원금에 투표합니다.') },
    { n: '04', label: t('Validator Collateral', '검증인 담보'),   desc: t('Validators post $SWARM as collateral in the Proof of Swarm network.', '검증인은 Proof of Swarm 네트워크에서 $SWARM을 담보로 예치합니다.') },
    { n: '05', label: t('Unit of Account', '회계 단위'),          desc: t('Compute and coordination tasks priced and settled in $SWARM.', '컴퓨팅 및 조율 작업의 가격 책정과 정산에 $SWARM이 사용됩니다.') },
    { n: '06', label: t('Badge Upgrades', '뱃지 업그레이드'),     desc: t('Burn $SWARM to upgrade Pioneer to Builder, Builder to OG.', '$SWARM을 소각하여 Pioneer에서 Builder로, Builder에서 OG로 업그레이드합니다.') },
    { n: '07', label: t('Agent Marketplace', '에이전트 마켓플레이스'), desc: t('All marketplace transactions settle in $SWARM from Phase 4 onward.', '4단계 이후 모든 마켓플레이스 거래가 $SWARM으로 정산됩니다.') },
  ];

  const tableHeaders = [
    t('Allocation', '배분'), '%', t('Tokens', '토큰 수량'),
    t('TGE', 'TGE'), t('Cliff', '클리프'), t('Vesting', '베스팅')
  ];

  return (
    <section id="tokenomics" className="py-20 sm:py-24 md:py-32"
      style={{ position: 'relative', zIndex: 10, background: 'var(--sb-bg-3)' }}
      data-testid="tokenomics-section">
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 400, background: 'radial-gradient(ellipse, rgba(48,213,200,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-10 relative">

        <div ref={ref} className={`mb-10 md:mb-12 anim-hidden ${inView ? 'anim-visible' : ''}`}>
          <div className="section-divider" />
          <p className="text-xs font-mono text-[#30d5c8] tracking-[0.3em] uppercase mb-3">{t('Tokenomics', '토크노믹스')}</p>
          <h2 className="font-outfit font-black text-white text-3xl sm:text-4xl md:text-5xl tracking-tight">
            {t('The', '')} <span className="gradient-text">$SWARM</span> {t('Token', '토큰')}
          </h2>
          <p className="text-white/50 mt-4 max-w-xl text-xs sm:text-sm md:text-base">
            {t('1,000,000,000 total supply. Hard capped, no mint function. BEP-20 on BNB Smart Chain.',
               '총 공급량 10억 개. 고정 공급, 민팅 없음. BNB 스마트 체인의 BEP-20 토큰입니다.')}
          </p>
        </div>

        {/* TGE Metrics */}
        <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10 anim-hidden ${inView ? 'anim-visible delay-100' : ''}`}>
          {[
            { label: t('Total Supply', '총 공급량'),         value: '1,000,000,000' },
            { label: t('Initial Circulating', '초기 유통량'), value: '288,000,000 (28.8%)' },
            { label: t('Locked at TGE', 'TGE 잠금 물량'),    value: '712,000,000 (71.2%)' },
          ].map(m => (
            <div key={m.label} className="glass rounded-xl px-4 py-4" data-testid={`tge-metric-${m.label.replace(/\s+/g, '-').toLowerCase()}`}>
              <p className="text-[10px] font-mono text-white/40 uppercase tracking-wider mb-1">{m.label}</p>
              <p className="text-sm font-mono font-bold text-[#30d5c8]">{m.value}</p>
            </div>
          ))}
        </div>

        {/* Chart + Vesting */}
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-start mb-10">
          {/* Donut chart */}
          <div className={`anim-hidden ${inView ? 'anim-visible delay-200' : ''}`}>
            <div className="glass rounded-2xl p-5 sm:p-7">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-outfit font-bold text-white text-base sm:text-lg">{t('Token Distribution', '토큰 배분')}</h3>
                  <p className="text-white/35 text-xs font-mono mt-0.5">{t('Total Supply: 1,000,000,000 $SWARM', '총 공급량: 1,000,000,000 $SWARM')}</p>
                </div>
                <span className="text-xs font-mono text-[#30d5c8] bg-[#30d5c8]/10 border border-[#30d5c8]/25 px-3 py-1 rounded-full">{t('TGE: Q3 2026', 'TGE: 2026년 3분기')}</span>
              </div>
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={tokenData} cx="50%" cy="50%" innerRadius={58} outerRadius={96} paddingAngle={1.5} dataKey="value"
                      onMouseEnter={(_, i) => setActive(i)} onMouseLeave={() => setActive(null)}>
                      {tokenData.map((entry, i) => (
                        <Cell key={entry.name_en} fill={entry.color}
                          opacity={active === null || active === i ? 1 : 0.35}
                          style={{ cursor: 'pointer', transition: 'opacity 0.2s', filter: active === i ? `drop-shadow(0 0 6px ${entry.color})` : 'none' }} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip lang={lang} />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-1.5 mt-3">
                {tokenData.map((item, i) => (
                  <div key={item.name_en} className="flex items-center gap-2 cursor-pointer rounded px-1 py-1 hover:bg-white/3 transition-colors"
                    onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}
                    data-testid={`token-legend-${item.name_en.replace(/[\s()]+/g, '-').toLowerCase()}`}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.color }} />
                    <span className="text-xs text-white/55 truncate">{lang === 'ko' ? item.name_ko : item.name_en}</span>
                    <span className="text-xs font-mono ml-auto flex-shrink-0" style={{ color: item.color }}>{item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Vesting schedule */}
          <div className={`anim-hidden ${inView ? 'anim-visible delay-300' : ''}`}>
            <div className="glass rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-white/5">
                <h3 className="font-outfit font-bold text-white text-base sm:text-lg">{t('Vesting Schedule', '베스팅 일정')}</h3>
                <p className="text-white/35 text-xs font-mono mt-0.5">{t('All vesting enforced via on-chain contracts at TGE', 'TGE 시 온체인 컨트랙트를 통한 베스팅 집행')}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      {tableHeaders.map(h => (
                        <th key={h} className="text-left px-4 py-2.5 text-white/32 font-normal tracking-wider uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {vestingData.map((row, i) => {
                      const color = tokenData.find(td => td.name_en === row.name_en)?.color || '#30d5c8';
                      return (
                        <tr key={row.name_en} className="hover:bg-white/3 transition-colors"
                          style={{ borderBottom: i < vestingData.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}
                          data-testid={`vesting-row-${row.name_en.replace(/[\s()]+/g, '-').toLowerCase()}`}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
                              <span className="text-white/82 font-semibold whitespace-nowrap">{lang === 'ko' ? row.name_ko : row.name_en}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-bold whitespace-nowrap" style={{ color }}>{row.pct}</td>
                          <td className="px-3 py-2.5 text-white/45 whitespace-nowrap">{row.tokens}</td>
                          <td className="px-3 py-2.5 text-white/55 whitespace-nowrap">{lang === 'ko' ? row.tge_ko : row.tge_en}</td>
                          <td className="px-3 py-2.5 text-white/45 whitespace-nowrap">{lang === 'ko' ? row.cliff_ko : row.cliff_en}</td>
                          <td className="px-4 py-2.5 text-white/45 whitespace-nowrap">{lang === 'ko' ? row.vest_ko : row.vest_en}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 border-t border-white/5" style={{ background: 'rgba(48,213,200,0.04)' }}>
                <p className="text-xs font-mono text-[#30d5c8]/80">
                  {t('Insider Lock: Strategic Round, Strategic Partners and Team (30% total) are fully locked for 12 months. Zero insider unlocks in Year 1.',
                     '내부자 잠금: 전략적 라운드, 전략적 파트너, 팀 물량(총 30%)은 TGE로부터 12개월간 완전히 잠금됩니다. 1년 차에는 내부자 물량 해제가 전혀 없습니다.')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Token Utility */}
        <div className={`anim-hidden ${inView ? 'anim-visible delay-300' : ''}`}>
          <h3 className="font-outfit font-bold text-white text-lg sm:text-xl mb-5">{t('Token Utility', '토큰 활용')}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {utility.map(u => (
              <div key={u.n} className="glass rounded-xl p-4 flex gap-3" data-testid={`utility-${u.n}`}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#30d5c8]/10 border border-[#30d5c8]/22">
                  <span className="text-[#30d5c8] font-mono text-xs font-bold">{u.n}</span>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold mb-0.5">{u.label}</p>
                  <p className="text-white/45 text-xs leading-relaxed">{u.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default TokenomicsSection;
